package com.flowyride.feature.planner

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.flowyride.core.domain.analysis.CurvatureAnalyzer
import com.flowyride.core.domain.analysis.distanceTo
import com.flowyride.core.domain.favorite.Favorite
import com.flowyride.core.domain.favorite.FavoriteException
import com.flowyride.core.domain.favorite.FavoriteRepository
import com.flowyride.core.domain.favorite.FavoriteSource
import com.flowyride.core.domain.favorite.FavoriteSummary
import com.flowyride.core.domain.favorite.FavoriteRules
import com.flowyride.core.domain.favorite.FavoriteStop
import com.flowyride.core.domain.favorite.NewFavorite
import com.flowyride.core.domain.gpx.GpxWaypoint
import com.flowyride.core.domain.loop.LoopRequest
import com.flowyride.core.domain.model.GeoPoint
import com.flowyride.core.domain.model.Place
import com.flowyride.core.domain.model.RoutePreferences
import com.flowyride.core.domain.place.NewSavedPlace
import com.flowyride.core.domain.place.SavedPlaces
import com.flowyride.core.domain.navigation.ActiveNavigation
import com.flowyride.core.domain.navigation.LocationSource
import com.flowyride.core.domain.navigation.NavigationPreferences
import com.flowyride.core.domain.navigation.NavigationRequest
import com.flowyride.core.domain.routing.TrackGuidance
import com.flowyride.core.domain.routing.RouteEngineRegistry
import com.flowyride.core.domain.routing.RoutingException
import com.flowyride.core.domain.routing.WaypointInsertion
import com.flowyride.core.domain.usecase.BuildGpxUseCase
import com.flowyride.core.domain.usecase.ComputeRouteUseCase
import com.flowyride.core.domain.usecase.GenerateLoopUseCase
import com.flowyride.core.domain.usecase.PlannedLoop
import com.flowyride.core.domain.usecase.GetSpeedCamerasUseCase
import com.flowyride.core.domain.usecase.GpxContent
import com.flowyride.core.domain.usecase.ImportedTrack
import com.flowyride.core.domain.usecase.ReadGpxUseCase
import com.flowyride.core.domain.usecase.ReverseGeocodeUseCase
import com.flowyride.core.domain.util.suspendRunCatching
import com.flowyride.core.domain.usecase.SearchPlacesUseCase
import com.flowyride.feature.planner.resources.Res
import com.flowyride.feature.planner.resources.error_engine
import com.flowyride.feature.planner.resources.error_favorite
import com.flowyride.feature.planner.resources.error_favorite_not_found
import com.flowyride.feature.planner.resources.error_gpx_invalid
import com.flowyride.feature.planner.resources.gpx_saved_to_favorites
import com.flowyride.feature.planner.resources.speed_limits_none
import com.flowyride.feature.planner.resources.error_location
import com.flowyride.feature.planner.resources.error_invalid_response
import com.flowyride.feature.planner.resources.error_loop_no_departure
import com.flowyride.feature.planner.resources.error_network
import com.flowyride.feature.planner.resources.error_no_loop
import com.flowyride.feature.planner.resources.error_no_route
import com.flowyride.feature.planner.resources.error_rate_limited
import com.flowyride.feature.planner.resources.error_search
import com.flowyride.feature.planner.resources.error_speed_cameras
import com.flowyride.feature.planner.resources.error_too_many_stops
import com.flowyride.feature.planner.resources.favorite_deleted
import com.flowyride.feature.planner.resources.favorite_saved
import com.flowyride.feature.planner.resources.place_saved
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.Job
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.mapNotNull
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.delay
import kotlinx.coroutines.withTimeoutOrNull
import org.jetbrains.compose.resources.StringResource
import kotlin.random.Random

class PlannerViewModel(
    engineRegistry: RouteEngineRegistry,
    private val searchPlaces: SearchPlacesUseCase,
    private val computeRoute: ComputeRouteUseCase,
    private val buildGpx: BuildGpxUseCase,
    private val reverseGeocode: ReverseGeocodeUseCase,
    private val getSpeedCameras: GetSpeedCamerasUseCase,
    private val generateLoop: GenerateLoopUseCase,
    private val favorites: FavoriteRepository,
    private val savedPlaces: SavedPlaces,
    private val readGpx: ReadGpxUseCase,
    private val activeNavigation: ActiveNavigation,
    private val navigationPreferences: NavigationPreferences,
    /** The device can guide with its GPS (Android); elsewhere only the simulation is offered. */
    canNavigate: Boolean,
    /** The device's position, for the "you are here" dot and the "my position" button. */
    private val location: LocationSource,
    /** Reading the position needs no prompt: the dot shows from the start. */
    private val hasLocationPermission: () -> Boolean,
    /** Speed limits along a line computed elsewhere (imported file, favorite saved before they existed). */
    private val guidance: TrackGuidance,
    /** New loops start from a random seed; tests pass their own. */
    private val newSeed: () -> Int = { Random.nextInt(0, 1_000_000) },
) : ViewModel() {

    private var nextStopId = 0L

    private val _state = MutableStateFlow(
        PlannerUiState(
            stops = listOf(newStop(), newStop()),
            engines = engineRegistry.engines.map {
                EngineUiModel(it.id, it.displayName, it.isAvailable, it.supportsPreferences, it.maxWaypoints)
            },
            selectedEngineId = engineRegistry.default.id,
            canNavigate = canNavigate,
            speedCameraAlerts = navigationPreferences.speedCameraAlerts.value,
        ),
    )
    val state: StateFlow<PlannerUiState> = _state.asStateFlow()

    private val _events = Channel<PlannerEvent>(Channel.BUFFERED)
    val events: Flow<PlannerEvent> = _events.receiveAsFlow()

    /** Only one field is edited at a time: a single debounced stream serves every stop. */
    private val searchRequests = MutableStateFlow<SearchRequest?>(null)
    private var routeJob: Job? = null

    /** Pending reverse geocoding per stop, cancelled when the rider types in that field meanwhile. */
    private val locateJobs = mutableMapOf<StopId, Job>()

    init {
        observeSearchRequests()
        loadSpeedCameras()
        loadSavedPlaces()
    }

    fun onAction(action: PlannerAction) {
        // An imported line is read-only: leave it (CloseImported) before editing.
        if (_state.value.imported != null && action.editsRide()) return
        val before = _state.value
        handle(action)
        // Only edits that changed the ride can be undone: typing, naming or a no-op move leave no step.
        if (action.isUndoableEdit() && before.mode == PlannerMode.ROUTE && before.rideKey() != _state.value.rideKey()) {
            _state.update { it.copy(history = it.history.edited(before.snapshot(), it.rideGeneration)) }
        }
    }

    private fun handle(action: PlannerAction) {
        when (action) {
            is PlannerAction.QueryChanged -> onQueryChanged(action.stop, action.query)
            is PlannerAction.PlaceSelected -> {
                updateStop(action.stop) {
                    it.copy(query = action.place.name, selected = action.place, suggestions = emptyList(), followsRider = false)
                }
                recomputeRoute()
            }
            is PlannerAction.FieldCleared -> {
                updateStop(action.stop) { StopUiState(it.id) }
                recomputeRoute()
            }
            PlannerAction.AddStop -> {
                if (!_state.value.canAddStop) return
                // No recompute: an empty stop does not change the route.
                _state.update { it.copy(stops = it.stops.toMutableList().apply { add(lastIndex, newStop()) }) }
            }
            is PlannerAction.RemoveStop -> {
                if (!_state.value.canRemoveStops) return
                _state.update { it.copy(stops = it.stops.filterNot { stop -> stop.id == action.stop }) }
                recomputeRoute()
            }
            is PlannerAction.MoveStop -> moveStop(action.stop, action.toIndex)
            is PlannerAction.MapPointPicked -> onMapPointPicked(action.point, action.target)
            is PlannerAction.StopMovedOnMap -> locate(action.stop, action.point)
            is PlannerAction.TrackDragged -> onTrackDragged(action.trackIndex, action.point)
            PlannerAction.ReverseStops -> {
                _state.update { it.copy(stops = it.stops.reversed()) }
                recomputeRoute()
            }
            is PlannerAction.EngineSelected -> {
                _state.update { it.copy(selectedEngineId = action.id) }
                recomputeRoute()
            }
            is PlannerAction.WindingnessChanged -> updatePreferences { it.copy(windingness = action.level) }
            is PlannerAction.AvoidMotorwaysChanged -> updatePreferences { it.copy(avoidMotorways = action.avoid) }
            is PlannerAction.ShowSpeedCamerasChanged -> _state.update { it.copy(showSpeedCameras = action.show) }
            PlannerAction.RetryRoute -> recomputeRoute()
            PlannerAction.ExportGpx -> exportGpx()
            is PlannerAction.ModeChanged -> _state.update { it.copy(mode = action.mode) }
            is PlannerAction.DestinationChosen -> chooseDestination(action.place)
            PlannerAction.DepartureFromRider -> if (_state.value.stops.first().selected == null) useRiderAsDeparture()
            is PlannerAction.SavePlace -> savePlace(action.name, action.place)
            is PlannerAction.LoopTargetKindChanged -> updateLoop { it.copy(targetKind = action.kind) }
            is PlannerAction.LoopDistanceChanged ->
                updateLoop { it.copy(distanceKm = action.km.coerceIn(LoopUiState.DISTANCE_KM)) }
            is PlannerAction.LoopDurationChanged ->
                updateLoop { it.copy(durationMinutes = action.minutes.coerceIn(LoopUiState.DURATION_MINUTES)) }
            is PlannerAction.LoopDirectionChanged -> updateLoop { it.copy(direction = action.direction) }
            PlannerAction.GenerateLoop -> startLoop()
            PlannerAction.RerollLoop -> _state.value.loop.lastRequest?.let { requestLoop(it.copy(seed = it.seed + 1)) }
            is PlannerAction.ImportGpx -> importGpx(action.fileName, action.content)
            is PlannerAction.SaveGpxToFavorites -> saveGpxToFavorites(action.fileName, action.content)
            PlannerAction.FetchSpeedLimits -> fetchSpeedLimits()
            PlannerAction.Undo -> undo()
            PlannerAction.Redo -> redo()
            is PlannerAction.GpxTrackChosen -> {
                _state.update { it.copy(gpxQuestion = null) }
                showImported(action.track, savedAs = null)
            }
            PlannerAction.GpxPointsConfirmed -> {
                val question = _state.value.gpxQuestion as? GpxQuestion.ComputeFromPoints ?: return
                _state.update { it.copy(gpxQuestion = null) }
                routeThrough(question.content.points)
            }
            PlannerAction.GpxQuestionDismissed -> _state.update { it.copy(gpxQuestion = null) }
            PlannerAction.CloseImported, PlannerAction.ClearStops -> reset()
            PlannerAction.LoadFavorites -> loadFavorites()
            is PlannerAction.OpenFavorite -> openFavorite(action.id)
            is PlannerAction.SaveFavorite -> saveFavorite(action.name)
            is PlannerAction.RenameFavorite -> renameFavorite(action.id, action.name)
            is PlannerAction.DeleteFavorite -> deleteFavorite(action.id)
            is PlannerAction.StartNavigation -> startNavigation(action.simulated)
            PlannerAction.LocateMe -> locateMe()
            is PlannerAction.LocationTrackingChanged ->
                if (action.active) {
                    if (hasLocationPermission()) startTracking()
                } else {
                    trackingJob?.cancel()
                    trackingJob = null
                }
            is PlannerAction.SpeedCameraAlertsChanged -> {
                navigationPreferences.setSpeedCameraAlerts(action.enabled)
                _state.update { it.copy(speedCameraAlerts = action.enabled) }
            }
        }
    }

    private fun PlannerAction.isUndoableEdit(): Boolean = when (this) {
        is PlannerAction.PlaceSelected, is PlannerAction.FieldCleared, is PlannerAction.RemoveStop,
        is PlannerAction.MoveStop, is PlannerAction.MapPointPicked, is PlannerAction.TrackDragged,
        is PlannerAction.StopMovedOnMap, PlannerAction.ReverseStops, is PlannerAction.EngineSelected,
        is PlannerAction.WindingnessChanged, is PlannerAction.AvoidMotorwaysChanged, is PlannerAction.DestinationChosen,
        -> true
        else -> false
    }

    private fun PlannerAction.editsRide(): Boolean = when (this) {
        is PlannerAction.QueryChanged, is PlannerAction.PlaceSelected, is PlannerAction.FieldCleared,
        PlannerAction.AddStop, is PlannerAction.RemoveStop, is PlannerAction.MoveStop,
        is PlannerAction.MapPointPicked, is PlannerAction.TrackDragged, is PlannerAction.StopMovedOnMap,
        PlannerAction.ReverseStops, is PlannerAction.EngineSelected, is PlannerAction.WindingnessChanged,
        is PlannerAction.AvoidMotorwaysChanged, PlannerAction.RetryRoute, PlannerAction.GenerateLoop,
        PlannerAction.RerollLoop, is PlannerAction.ModeChanged, is PlannerAction.DestinationChosen,
        PlannerAction.DepartureFromRider,
        -> true
        else -> false
    }

    private fun newStop() = StopUiState(StopId(nextStopId++))

    /** Loaded in the background: the map works without them. */
    private fun loadSpeedCameras(reportFailure: Boolean = true) {
        viewModelScope.launch {
            getSpeedCameras()
                .onSuccess { cameras -> _state.update { it.copy(speedCameras = cameras) } }
                .onFailure { if (reportFailure) _events.send(PlannerEvent.ShowError(Res.string.error_speed_cameras)) }
        }
    }

    private fun onQueryChanged(stop: StopId, query: String) {
        locateJobs.remove(stop)?.cancel()
        // Typing invalidates the previous pick; only the edited field shows suggestions.
        _state.update { state ->
            state.copy(
                stops = state.stops.map {
                    if (it.id == stop) it.copy(query = query, selected = null, followsRider = false) else it.copy(suggestions = emptyList())
                },
            )
        }
        searchRequests.value = SearchRequest(stop, query)
    }

    @OptIn(FlowPreview::class)
    private fun observeSearchRequests() {
        viewModelScope.launch {
            searchRequests
                .filterNotNull()
                .debounce(SEARCH_DEBOUNCE_MS)
                .collectLatest { (stop, query) ->
                    val current = _state.value.stops.firstOrNull { it.id == stop }
                    if (current == null || current.selected != null) return@collectLatest
                    updateStop(stop) { it.copy(isSearching = true) }
                    searchPlaces(query)
                        .onSuccess { places ->
                            // The rider's own addresses first.
                            val suggestions = savedMatching(query) + places
                            updateStop(stop) { it.copy(suggestions = suggestions, isSearching = false) }
                        }
                        .onFailure {
                            updateStop(stop) { it.copy(suggestions = emptyList(), isSearching = false) }
                            _events.send(PlannerEvent.ShowError(Res.string.error_search))
                        }
                }
        }
    }

    private fun moveStop(stop: StopId, toIndex: Int) {
        val stops = _state.value.stops
        val from = stops.indexOfFirst { it.id == stop }
        if (from < 0 || toIndex !in stops.indices || from == toIndex) return
        _state.update { it.copy(stops = stops.toMutableList().apply { add(toIndex, removeAt(from)) }) }
        recomputeRoute()
    }

    private fun onMapPointPicked(point: GeoPoint, target: MapPickTarget) {
        val stops = _state.value.stops
        val stop = when (target) {
            MapPickTarget.NEXT_EMPTY -> stops.firstOrNull { it.selected == null }?.id
            MapPickTarget.DEPARTURE -> stops.first().id
            MapPickTarget.ARRIVAL -> stops.last().id
            MapPickTarget.STOP -> insertStopNear(point)
        } ?: return
        locate(stop, point)
    }

    /**
     * Puts [stop] at [point] right away (its marker shows there while the spot is being named),
     * then names it and recomputes the route.
     */
    private fun locate(stop: StopId, point: GeoPoint, followsRider: Boolean = false) {
        locateJobs.remove(stop)?.cancel()
        updateStop(stop) {
            it.copy(
                query = "",
                selected = Place(name = "", detail = null, point = point),
                suggestions = emptyList(),
                isSearching = true,
                followsRider = followsRider,
            )
        }
        locateJobs[stop] = viewModelScope.launch {
            val place = reverseGeocode(point)
            updateStop(stop) { it.copy(query = place.name, selected = place, isSearching = false) }
            locateJobs.remove(stop)
            recomputeRoute()
        }
    }

    private fun onTrackDragged(trackIndex: Int, point: GeoPoint) {
        val state = _state.value
        val ready = state.route as? RouteUiState.Ready ?: return
        if (!state.canAddStop) return
        // The grabbed leg ends at this routed stop: the new one goes right before it.
        val legEnd = ready.stops.getOrNull(ready.route.legIndexAt(trackIndex) + 1) ?: return
        val index = state.stops.indexOfFirst { it.id == legEnd.id }.takeIf { it > 0 } ?: return
        val stop = newStop()
        _state.update { it.copy(stops = it.stops.toMutableList().apply { add(index, stop) }) }
        locate(stop.id, point)
    }

    /** @return the new stop, inserted where it makes the smallest detour; null when the engine limit is reached. */
    private fun insertStopNear(point: GeoPoint): StopId? {
        val state = _state.value
        if (!state.canAddStop) return null
        val located = state.stops.withIndex().filter { it.value.selected != null }
        val bothEndsKnown = state.stops.first().selected != null && state.stops.last().selected != null
        val index = if (bothEndsKnown) {
            located[WaypointInsertion.bestIndex(located.map { it.value.selected!!.point }, point)].index
        } else {
            state.stops.lastIndex
        }
        val stop = newStop()
        _state.update { it.copy(stops = it.stops.toMutableList().apply { add(index, stop) }) }
        return stop.id
    }

    private fun updatePreferences(transform: (RoutePreferences) -> RoutePreferences) {
        _state.update { it.copy(preferences = transform(it.preferences)) }
        recomputeRoute()
    }

    /**
     * Recomputes as soon as departure and arrival are known, so every change is reflected instantly.
     *
     * @param thenNavigate starts the navigation (simulated or not) once the route is computed.
     */
    private fun recomputeRoute(thenNavigate: Boolean? = null) {
        routeJob?.cancel()
        val current = _state.value
        val stops = current.routableStops
        val engine = current.selectedEngine
        if (stops == null || engine == null) {
            _state.update { it.copy(route = RouteUiState.Idle) }
            return
        }
        _state.update { it.copy(route = RouteUiState.Loading) }
        routeJob = viewModelScope.launch {
            val result = computeRoute(engine.id, stops.map { it.place.point }, current.preferences)
            _state.update {
                it.copy(
                    route = result.fold(
                        onSuccess = { planned ->
                            RouteUiState.Ready(
                                route = planned.route,
                                engineName = engine.name,
                                curvature = CurvatureAnalyzer.analyze(planned.route.track.map { p -> p.point }),
                                stops = stops,
                                speedCamerasOnRoute = planned.speedCamerasOnRoute.size,
                            )
                        },
                        onFailure = { error -> RouteUiState.Failed(error.toMessage(), error.message) },
                    ),
                )
            }
            if (result.isSuccess && thenNavigate != null) launchNavigation(thenNavigate)
            // The initial load may have failed (network hiccup); once obtained, the list is cached.
            if (result.isSuccess && _state.value.speedCameras.isEmpty()) loadSpeedCameras(reportFailure = false)
        }
    }

    private fun startLoop() {
        val state = _state.value
        val departure = state.stops.first().selected
        if (departure == null) {
            viewModelScope.launch { _events.send(PlannerEvent.ShowError(Res.string.error_loop_no_departure)) }
            return
        }
        requestLoop(LoopRequest(departure.point, state.loop.target, state.loop.direction, state.preferences, newSeed()))
    }

    private fun requestLoop(request: LoopRequest) {
        val state = _state.value
        val engine = state.selectedEngine ?: return
        if (state.loop.isGenerating) return
        updateLoop { it.copy(isGenerating = true) }
        viewModelScope.launch {
            generateLoop(request)
                .onSuccess { applyLoop(request, it, engine.name) }
                .onFailure { error ->
                    updateLoop { it.copy(isGenerating = false) }
                    _events.send(PlannerEvent.ShowError(error.toLoopMessage()))
                }
        }
    }

    /**
     * The loop replaces every stop and shows up at once: the server already computed its route.
     * Generated stops are named afterwards, like points picked on the map.
     */
    private fun applyLoop(request: LoopRequest, loop: PlannedLoop, engineName: String) {
        cancelRideJobs()

        val currentDeparture = _state.value.stops.first()
        // The departure may have changed since the loop was asked for ("another loop").
        val knownDeparture = currentDeparture.selected?.takeIf { it.point == request.start }
        fun pending(point: GeoPoint) = newStop().copy(selected = Place(name = "", detail = null, point = point), isSearching = true)
        val departure = if (knownDeparture != null) currentDeparture else pending(request.start)
        val stops = listOf(departure) +
            loop.waypoints.drop(1).dropLast(1).map(::pending) +
            (knownDeparture?.let { newStop().copy(query = it.name, selected = it, followsRider = currentDeparture.followsRider) } ?: pending(request.start))
        val routed = stops.map { RoutedStop(it.id, it.selected!!) }

        _state.update {
            it.copy(
                stops = stops,
                mode = PlannerMode.ROUTE,
                loop = it.loop.copy(isGenerating = false, lastRequest = request),
                rideGeneration = it.rideGeneration + 1,
                route = RouteUiState.Ready(
                    route = loop.planned.route,
                    engineName = engineName,
                    curvature = CurvatureAnalyzer.analyze(loop.planned.route.track.map { p -> p.point }),
                    stops = routed,
                    speedCamerasOnRoute = loop.planned.speedCamerasOnRoute.size,
                ),
            )
        }
        stops.filter { it.isSearching }.forEach { name(it.id, it.selected!!.point) }
    }

    /** Names a stop without recomputing: the route through it is already known. */
    private fun name(stop: StopId, point: GeoPoint) {
        locateJobs[stop] = viewModelScope.launch {
            val place = reverseGeocode(point)
            updateStop(stop) { it.copy(query = place.name, selected = place, isSearching = false) }
            _state.update { state ->
                val ready = state.route as? RouteUiState.Ready ?: return@update state
                state.copy(route = ready.copy(stops = ready.stops.map { if (it.id == stop) it.copy(place = place) else it }))
            }
            locateJobs.remove(stop)
        }
    }

    private fun updateLoop(transform: (LoopUiState) -> LoopUiState) {
        _state.update { it.copy(loop = transform(it.loop)) }
    }

    private fun exportGpx() {
        val ready = _state.value.route as? RouteUiState.Ready ?: return
        viewModelScope.launch {
            _events.send(PlannerEvent.SaveGpx(buildGpx(ready.route, ready.stops.map { it.place })))
        }
    }

    private fun updateStop(stop: StopId, transform: (StopUiState) -> StopUiState) {
        _state.update { state -> state.copy(stops = state.stops.map { if (it.id == stop) transform(it) else it }) }
    }

    private fun Throwable.toMessage(): StringResource = when (this) {
        is RoutingException.NoRouteFound -> Res.string.error_no_route
        is RoutingException.Network -> Res.string.error_network
        is RoutingException.InvalidResponse -> Res.string.error_invalid_response
        is RoutingException.TooManyWaypoints -> Res.string.error_too_many_stops
        is RoutingException.RateLimited -> Res.string.error_rate_limited
        else -> Res.string.error_engine
    }

    private fun Throwable.toLoopMessage(): StringResource = when (this) {
        is RoutingException.NoRouteFound -> Res.string.error_no_loop
        else -> toMessage()
    }

    // --- My position -----------------------------------------------------------------------------------

    private var trackingJob: Job? = null
    private var locateTimeout: Job? = null

    /** Follows the position while the planner is on screen. */
    private fun startTracking() {
        if (trackingJob?.isActive == true) return
        trackingJob = viewModelScope.launch {
            suspendRunCatching {
                location.fixes().collect { fix ->
                    val centre = _state.value.isLocating
                    if (centre) locateTimeout?.cancel()
                    _state.update {
                        it.copy(
                            userLocation = fix.point,
                            isLocating = false,
                            locatedAt = if (centre) fix.point else it.locatedAt,
                            locateGeneration = if (centre) it.locateGeneration + 1 else it.locateGeneration,
                        )
                    }
                }
            }
        }
    }

    /** Centres on the rider: at once when the position is known, else on the first one to come. */
    private fun locateMe() {
        startTracking()
        val known = _state.value.userLocation
        if (known != null) {
            _state.update { it.copy(locatedAt = known, locateGeneration = it.locateGeneration + 1) }
            return
        }
        if (_state.value.isLocating) return
        _state.update { it.copy(isLocating = true) }
        locateTimeout = viewModelScope.launch {
            delay(LOCATE_TIMEOUT_MS)
            if (_state.value.isLocating) {
                _state.update { it.copy(isLocating = false) }
                _events.send(PlannerEvent.ShowError(Res.string.error_location))
            }
        }
    }

    // --- Saved addresses -------------------------------------------------------------------------------

    /** Shared with the settings: an address edited there shows here at once. Silent on failure: a shortcut. */
    private fun loadSavedPlaces() {
        viewModelScope.launch { savedPlaces.places.collect { places -> _state.update { it.copy(savedPlaces = places) } } }
        viewModelScope.launch { suspendRunCatching { savedPlaces.refresh() } }
    }

    private fun savedMatching(query: String): List<Place> {
        val wanted = query.trim()
        if (wanted.isEmpty()) return emptyList()
        return _state.value.savedPlaces.filter { it.name.contains(wanted, ignoreCase = true) }.map { it.toPlace() }
    }

    private fun savePlace(name: String, place: Place) {
        viewModelScope.launch {
            suspendRunCatching { savedPlaces.save(NewSavedPlace(name.trim(), place.detail, place.point)) }
                .onSuccess { _events.send(PlannerEvent.ShowMessage(Res.string.place_saved)) }
                .onFailure { _events.send(PlannerEvent.ShowError(it.toFavoriteMessage())) }
        }
    }

    // --- From the rider's position ---------------------------------------------------------------------

    private var riderDepartureJob: Job? = null

    private fun chooseDestination(place: Place) {
        val arrival = _state.value.stops.last().id
        locateJobs.remove(arrival)?.cancel()
        updateStop(arrival) {
            it.copy(query = place.name, selected = place, suggestions = emptyList(), isSearching = false, followsRider = false)
        }
        recomputeRoute()
    }

    /** The departure becomes where the rider stands: at once when the position is known, else at the first fix. */
    private fun useRiderAsDeparture() {
        val departure = _state.value.stops.first().id
        _state.value.userLocation?.let {
            locate(departure, it, followsRider = true)
            return
        }
        startTracking()
        // "Ma position" right away, located once the first fix comes.
        updateStop(departure) { it.copy(followsRider = true, isSearching = true) }
        riderDepartureJob?.cancel()
        riderDepartureJob = viewModelScope.launch {
            val point = withTimeoutOrNull(LOCATE_TIMEOUT_MS) { _state.mapNotNull { it.userLocation }.first() }
            val stop = _state.value.stops.firstOrNull { it.id == departure }
            // Still waiting: the rider may have filled it meanwhile.
            if (stop == null || stop.selected != null || !stop.followsRider) return@launch
            if (point != null) {
                locate(departure, point, followsRider = true)
            } else {
                updateStop(departure) { it.copy(followsRider = false, isSearching = false) }
                _events.send(PlannerEvent.ShowError(Res.string.error_location))
            }
        }
    }

    // --- Navigation ------------------------------------------------------------------------------------

    private var navigationCount = 0L

    /** "Ma position" stops are moved to where the rider is now (route recomputed) if they moved away since. */
    private fun startNavigation(simulated: Boolean) {
        val state = _state.value
        if (state.route !is RouteUiState.Ready) return
        val rider = state.userLocation
        val moved = rider != null && state.stops.any { stop ->
            stop.followsRider && stop.selected != null && stop.selected.point.distanceTo(rider) > RIDER_MOVED_METERS
        }
        if (!moved) {
            launchNavigation(simulated)
            return
        }
        _state.update { current ->
            current.copy(
                stops = current.stops.map { stop ->
                    val place = stop.selected
                    if (stop.followsRider && place != null) stop.copy(selected = place.copy(point = rider)) else stop
                },
            )
        }
        recomputeRoute(thenNavigate = simulated)
    }

    private fun launchNavigation(simulated: Boolean) {
        val state = _state.value
        val ready = state.route as? RouteUiState.Ready ?: return
        activeNavigation.start(
            NavigationRequest(
                id = navigationCount++,
                name = state.suggestedFavoriteName,
                route = ready.route,
                stopNames = ready.stops.map { it.place.name },
                speedCameras = state.speedCameras,
                simulated = simulated || !state.canNavigate,
            ),
        )
    }

    // --- Starting over ---------------------------------------------------------------------------------

    /** Drops every pending job tied to the current stops and route. */
    private fun cancelRideJobs() {
        routeJob?.cancel()
        locateJobs.values.forEach { it.cancel() }
        locateJobs.clear()
        riderDepartureJob?.cancel()
    }

    /** Back to an empty route. */
    private fun reset() {
        cancelRideJobs()
        _state.update {
            it.copy(
                stops = listOf(newStop(), newStop()),
                route = RouteUiState.Idle,
                imported = null,
                mode = PlannerMode.ROUTE,
                rideGeneration = it.rideGeneration + 1,
            )
        }
    }

    // --- GPX import -------------------------------------------------------------------------------------

    private fun importGpx(fileName: String, content: String) {
        viewModelScope.launch {
            readGpx(fileName, content)
                .onSuccess { gpx ->
                    when (gpx) {
                        is GpxContent.Tracks ->
                            if (gpx.tracks.size == 1) {
                                showImported(gpx.tracks.single(), savedAs = null)
                            } else {
                                _state.update { it.copy(gpxQuestion = GpxQuestion.ChooseTrack(gpx.tracks)) }
                            }
                        is GpxContent.PointsOnly -> _state.update { it.copy(gpxQuestion = GpxQuestion.ComputeFromPoints(gpx)) }
                    }
                }
                .onFailure { _events.send(PlannerEvent.ShowError(Res.string.error_gpx_invalid)) }
        }
    }

    /** Shows [track] as is. Departure and arrival take the name of a waypoint standing there, if any. */
    private fun showImported(track: ImportedTrack, savedAs: FavoriteSummary?) {
        cancelRideJobs()
        val points = track.route.track
        fun endName(end: GeoPoint) = track.waypoints
            .firstOrNull { it.name.isNotBlank() && it.point.distanceTo(end) <= END_WAYPOINT_RADIUS_METERS }
            ?.name ?: track.name
        val departure = newStop().let { it.copy(query = endName(points.first().point), selected = Place(endName(points.first().point), null, points.first().point)) }
        val arrival = newStop().let { it.copy(query = endName(points.last().point), selected = Place(endName(points.last().point), null, points.last().point)) }
        val ready = RouteUiState.Ready(
            route = track.route,
            engineName = "GPX",
            curvature = CurvatureAnalyzer.analyze(points.map { it.point }),
            stops = listOf(RoutedStop(departure.id, departure.selected!!), RoutedStop(arrival.id, arrival.selected!!)),
            isImported = true,
            savedAs = savedAs,
        )
        _state.update {
            it.copy(
                stops = listOf(departure, arrival),
                mode = PlannerMode.ROUTE,
                imported = ImportedRideUiState(track.name, track.waypoints),
                route = ready,
                rideGeneration = it.rideGeneration + 1,
            )
        }
        countSpeedCameras(ready)
    }

    /** Routes through the points of a file without a line; unnamed points are named afterwards. */
    private fun routeThrough(points: List<GpxWaypoint>) {
        val max = _state.value.selectedEngine?.maxWaypoints ?: Int.MAX_VALUE
        val kept = if (points.size <= max) points else points.evenlySampled(max)
        cancelRideJobs()
        val stops = kept.map { newStop().copy(query = it.name, selected = Place(it.name, null, it.point), isSearching = it.name.isBlank()) }
        _state.update { it.copy(stops = stops, mode = PlannerMode.ROUTE, imported = null, rideGeneration = it.rideGeneration + 1) }
        recomputeRoute()
        stops.filter { it.isSearching }.forEach { name(it.id, it.selected!!.point) }
    }

    /**
     * Saves a file to the favorites without touching the ride on screen: every line as is, or the line computed
     * through its points (unnamed points named from the map).
     */
    private fun saveGpxToFavorites(fileName: String, content: String) {
        viewModelScope.launch {
            val gpx = readGpx(fileName, content).getOrElse {
                _events.send(PlannerEvent.ShowError(Res.string.error_gpx_invalid))
                return@launch
            }
            val newFavorites = when (gpx) {
                is GpxContent.Tracks -> gpx.tracks.map { track ->
                    NewFavorite(
                        name = track.name.take(FavoriteRules.NAME_MAX),
                        source = FavoriteSource.IMPORTED,
                        stops = track.waypoints.map { FavoriteStop(it.name, it.point) },
                        route = track.route,
                        preferences = null,
                    )
                }
                is GpxContent.PointsOnly -> {
                    val state = _state.value
                    val engine = state.selectedEngine ?: return@launch
                    val points = gpx.points.let { if (it.size <= engine.maxWaypoints) it else it.evenlySampled(engine.maxWaypoints) }
                    val planned = computeRoute(engine.id, points.map { it.point }, state.preferences).getOrElse {
                        _events.send(PlannerEvent.ShowError(it.toMessage()))
                        return@launch
                    }
                    val stops = points.map { FavoriteStop(it.name.ifBlank { reverseGeocode(it.point).name }, it.point) }
                    val name = gpx.name ?: "${stops.first().name} → ${stops.last().name}"
                    listOf(NewFavorite(name.take(FavoriteRules.NAME_MAX), FavoriteSource.PLANNED, stops, planned.route, state.preferences))
                }
            }
            for (favorite in newFavorites) {
                val summary = suspendRunCatching { favorites.save(favorite) }.getOrElse {
                    _events.send(PlannerEvent.ShowError(it.toFavoriteMessage()))
                    return@launch
                }
                updateFavorites { it.copy(items = listOf(summary) + it.items) }
            }
            _events.send(PlannerEvent.ShowMessage(Res.string.gpx_saved_to_favorites))
        }
    }

    // --- Undo / redo ------------------------------------------------------------------------------------

    private fun undo() {
        val state = _state.value
        if (!state.canUndo) return
        val history = state.history
        restore(history.undo.last(), history.copy(undo = history.undo.dropLast(1), redo = history.redo + state.snapshot()))
    }

    private fun redo() {
        val state = _state.value
        if (!state.canRedo) return
        val history = state.history
        restore(history.redo.last(), history.copy(undo = history.undo + state.snapshot(), redo = history.redo.dropLast(1)))
    }

    /** The route comes back as it was; one still being computed or named then is computed or named again. */
    private fun restore(snapshot: RideSnapshot, history: RideHistory) {
        cancelRideJobs()
        val ready = snapshot.route as? RouteUiState.Ready
        _state.update {
            it.copy(
                stops = snapshot.stops.map { stop -> stop.copy(suggestions = emptyList()) },
                preferences = snapshot.preferences,
                selectedEngineId = snapshot.selectedEngineId,
                route = ready ?: RouteUiState.Idle,
                history = history,
            )
        }
        if (ready == null) recomputeRoute()
        snapshot.stops.filter { it.isSearching }.forEach { stop -> stop.selected?.let { name(stop.id, it.point) } }
    }

    // --- Speed limits -----------------------------------------------------------------------------------

    /** The line stays as is: only its limits are added (and turn instructions, if it had none). */
    private fun fetchSpeedLimits() {
        val ready = _state.value.route as? RouteUiState.Ready ?: return
        if (ready.isFetchingSpeedLimits || ready.route.speedLimits.isNotEmpty()) return
        _state.update { it.copy(route = ready.copy(isFetchingSpeedLimits = true)) }
        viewModelScope.launch {
            val guide = suspendRunCatching { guidance.guideAlong(ready.route.track.map { it.point }) }
            _state.update { state ->
                val current = state.route as? RouteUiState.Ready
                // The ride changed meanwhile: the limits belong to another line.
                if (current == null || current.route != ready.route) return@update state
                val route = guide.getOrNull()?.let { found ->
                    current.route.copy(
                        speedLimits = found.speedLimits,
                        maneuvers = current.route.maneuvers.ifEmpty { found.maneuvers },
                    )
                } ?: current.route
                state.copy(route = current.copy(route = route, isFetchingSpeedLimits = false))
            }
            guide.onFailure { _events.send(PlannerEvent.ShowError(it.toMessage())) }
                .onSuccess { if (it.speedLimits.isEmpty()) _events.send(PlannerEvent.ShowMessage(Res.string.speed_limits_none)) }
        }
    }

    // --- Favorites --------------------------------------------------------------------------------------

    private fun loadFavorites() {
        updateFavorites { it.copy(isLoading = true, failed = false) }
        viewModelScope.launch {
            suspendRunCatching { favorites.list() }
                .onSuccess { items -> updateFavorites { it.copy(items = items, isLoading = false) } }
                .onFailure { updateFavorites { it.copy(isLoading = false, failed = true) } }
        }
    }

    private fun openFavorite(id: Long) {
        if (_state.value.favorites.isBusy) return
        updateFavorites { it.copy(isBusy = true) }
        viewModelScope.launch {
            suspendRunCatching { favorites.get(id) }
                .onSuccess { favorite ->
                    updateFavorites { it.copy(isBusy = false) }
                    when (favorite.summary.source) {
                        FavoriteSource.IMPORTED -> showImported(
                            ImportedTrack(favorite.summary.name, favorite.route, favorite.stops.map { GpxWaypoint(it.name, it.point) }),
                            savedAs = favorite.summary,
                        )
                        FavoriteSource.PLANNED -> showPlanned(favorite)
                    }
                    _events.send(PlannerEvent.FavoriteOpened)
                }
                .onFailure { error ->
                    updateFavorites { it.copy(isBusy = false) }
                    _events.send(PlannerEvent.ShowError(error.toFavoriteMessage()))
                    if (error is FavoriteException.NotFound) loadFavorites()
                }
        }
    }

    /** Shows the saved route without asking the engine again; editing a stop recomputes as usual. */
    private fun showPlanned(favorite: Favorite) {
        cancelRideJobs()
        val saved = favorite.stops.map { newStop().copy(query = it.name, selected = Place(it.name, null, it.point)) }
        // Always a departure and an arrival, even from a malformed favorite.
        val stops = saved + List((2 - saved.size).coerceAtLeast(0)) { newStop() }
        val routed = stops.mapNotNull { stop -> stop.selected?.let { RoutedStop(stop.id, it) } }
        val engine = _state.value.selectedEngine
        val ready = RouteUiState.Ready(
            route = favorite.route,
            engineName = engine?.name.orEmpty(),
            curvature = CurvatureAnalyzer.analyze(favorite.route.track.map { it.point }),
            stops = routed,
            savedAs = favorite.summary,
        )
        _state.update {
            it.copy(
                stops = stops,
                mode = PlannerMode.ROUTE,
                imported = null,
                preferences = favorite.preferences ?: it.preferences,
                route = if (routed.size >= 2) ready else RouteUiState.Idle,
                rideGeneration = it.rideGeneration + 1,
            )
        }
        if (routed.size >= 2) countSpeedCameras(ready)
    }

    private fun saveFavorite(name: String) {
        val state = _state.value
        val ready = state.route as? RouteUiState.Ready ?: return
        if (state.favorites.isBusy) return
        val imported = state.imported
        val favorite = NewFavorite(
            name = name.trim(),
            source = if (imported != null) FavoriteSource.IMPORTED else FavoriteSource.PLANNED,
            stops = imported?.waypoints?.map { FavoriteStop(it.name, it.point) } ?: ready.stops.map { FavoriteStop(it.place.name, it.place.point) },
            route = ready.route,
            preferences = if (imported != null) null else state.preferences,
        )
        updateFavorites { it.copy(isBusy = true) }
        viewModelScope.launch {
            suspendRunCatching { favorites.save(favorite) }
                .onSuccess { summary ->
                    _state.update { current ->
                        val route = current.route
                        current.copy(
                            // Only if the route did not change meanwhile.
                            route = if (route is RouteUiState.Ready && route.route == ready.route) route.copy(savedAs = summary) else route,
                            favorites = current.favorites.copy(isBusy = false, items = listOf(summary) + current.favorites.items),
                        )
                    }
                    _events.send(PlannerEvent.ShowMessage(Res.string.favorite_saved))
                }
                .onFailure { error ->
                    updateFavorites { it.copy(isBusy = false) }
                    _events.send(PlannerEvent.ShowError(error.toFavoriteMessage()))
                }
        }
    }

    private fun renameFavorite(id: Long, name: String) {
        viewModelScope.launch {
            suspendRunCatching { favorites.rename(id, name.trim()) }
                .onSuccess { summary ->
                    _state.update { state ->
                        val route = state.route
                        state.copy(
                            favorites = state.favorites.copy(items = state.favorites.items.map { if (it.id == id) summary else it }),
                            route = if (route is RouteUiState.Ready && route.savedAs?.id == id) route.copy(savedAs = summary) else route,
                        )
                    }
                }
                .onFailure { _events.send(PlannerEvent.ShowError(it.toFavoriteMessage())) }
        }
    }

    private fun deleteFavorite(id: Long) {
        viewModelScope.launch {
            suspendRunCatching { favorites.delete(id) }
                .onSuccess {
                    _state.update { state ->
                        val route = state.route
                        state.copy(
                            favorites = state.favorites.copy(items = state.favorites.items.filterNot { it.id == id }),
                            route = if (route is RouteUiState.Ready && route.savedAs?.id == id) route.copy(savedAs = null) else route,
                        )
                    }
                    _events.send(PlannerEvent.ShowMessage(Res.string.favorite_deleted))
                }
                .onFailure { _events.send(PlannerEvent.ShowError(it.toFavoriteMessage())) }
        }
    }

    private fun updateFavorites(transform: (FavoritesUiState) -> FavoritesUiState) {
        _state.update { it.copy(favorites = transform(it.favorites)) }
    }

    /** Cameras of a route shown without computing it (favorite, imported file). */
    private fun countSpeedCameras(ready: RouteUiState.Ready) {
        routeJob = viewModelScope.launch {
            val planned = computeRoute.analyze(ready.route)
            _state.update { state ->
                val current = state.route
                if (current is RouteUiState.Ready && current.route == ready.route) {
                    state.copy(route = current.copy(speedCamerasOnRoute = planned.speedCamerasOnRoute.size))
                } else {
                    state
                }
            }
        }
    }

    private fun Throwable.toFavoriteMessage(): StringResource = when (this) {
        is FavoriteException.NotFound -> Res.string.error_favorite_not_found
        is FavoriteException.Unreachable -> Res.string.error_network
        else -> Res.string.error_favorite
    }

    private data class SearchRequest(val stop: StopId, val query: String)

    private companion object {
        const val SEARCH_DEBOUNCE_MS = 350L

        const val LOCATE_TIMEOUT_MS = 20_000L

        /** "Ma position" further than this from the rider at start: the route is computed again from there. */
        const val RIDER_MOVED_METERS = 300.0

        /** A waypoint this close to an end of an imported line names it. */
        const val END_WAYPOINT_RADIUS_METERS = 300.0
    }
}

/** [count] items spread along the list, first and last included. */
private fun <T> List<T>.evenlySampled(count: Int): List<T> {
    if (size <= count || count < 2) return take(count)
    return List(count) { i -> this[(i.toLong() * lastIndex / (count - 1)).toInt()] }
}
